import { useEffect, useRef, useState } from 'react';

const milestones = [
  {
    year: '8 歲',
    title: '與古箏的第一次相遇',
    desc: '在一場廟會表演中，第一次聽到古箏的聲音。那個聲音讓我停下來站了很久，直到曲子結束——像是有什麼東西被打開了。',
    icon: 'ri-seedling-line',
  },
  {
    year: '12 歲',
    title: '開始正式學習',
    desc: '進入傳統古箏班，接受系統化技法訓練。那幾年扎實而辛苦，每天練習指法、背譜、為考級準備。技術在進步，但彈琴也慢慢變成一件需要完成的任務。',
    icon: 'ri-music-2-line',
  },
  {
    year: '大學',
    title: '在音樂中感到窒息',
    desc: '就讀音樂相關科系，古箏成為課業與考核的中心。面對表演壓力和同儕競爭，開始對古箏產生距離感，彈琴常常讓我焦慮，而不是平靜。',
    icon: 'ri-emotion-unhappy-line',
  },
  {
    year: '25 歲',
    title: '重新找回與音樂的連結',
    desc: '畢業後離開競爭環境，花了將近一年，放下所有「應該」，只是自己彈、自己聽。慢慢地，彈琴開始讓我感到安靜了。',
    icon: 'ri-heart-3-line',
  },
  {
    year: '28 歲',
    title: '開始教學，選擇不一樣的方式',
    desc: '開始帶第一批學生，刻意不複製自己過去的學習經歷。沒有固定進度表、不強調速成、每堂課從學生的狀態出發，看到學生因安心而打開，確認這條路是對的。',
    icon: 'ri-user-heart-line',
  },
  {
    year: '現在',
    title: '建立鳴箏，持續陪伴',
    desc: '累積多年教學經驗，建立鳴箏這個品牌。教過孩子、上班族、媽媽、退休長輩，每個人帶給我的反饋都讓我更確定：讓音樂成為生活的一部分，比學到多少技術更重要。',
    icon: 'ri-store-3-line',
  },
];

const credentials = [
  { icon: 'ri-award-line', label: '古箏演奏資歷', value: '20 年以上' },
  { icon: 'ri-group-line', label: '累積教學學員', value: '逾 200 位' },
  { icon: 'ri-calendar-line', label: '正式教學年資', value: '8 年以上' },
  { icon: 'ri-map-pin-2-line', label: '實體教室地點', value: '台中豐原' },
];

function useVisible(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

export default function TeacherJourneySection() {
  const { ref, visible } = useVisible();
  const credRef = useVisible();

  return (
    <>
      {/* Credentials Strip */}
      <section className="py-14 bg-[#2c2420]">
        <div
          ref={credRef.ref}
          className={`w-full px-6 md:px-12 lg:px-20 transition-all duration-700 ${credRef.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {credentials.map((c, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#3e3028]">
                  <i className={`${c.icon} text-[#c8b89a] text-lg`}></i>
                </div>
                <p
                  className="font-serif-tc text-white leading-none"
                  style={{ fontSize: 'clamp(1.375rem, 2.5vw, 1.75rem)' }}
                >
                  {c.value}
                </p>
                <p className="text-[0.8125rem] text-[#7a6e62] tracking-wide">{c.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="py-24 md:py-32 bg-[#faf8f5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div
            ref={ref}
            className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-6 h-px bg-[#b08060]"></div>
                  <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">
                    Journey
                  </span>
                </div>
                <h2
                  className="font-serif-tc text-[#2c2420] leading-[1.3]"
                  style={{ fontSize: 'clamp(1.625rem, 3vw, 2.25rem)' }}
                >
                  我的學習歷程<br />與教學之路
                </h2>
              </div>
              <p className="text-[0.9375rem] leading-[1.75] text-[#7a6e62] max-w-sm">
                二十年的古箏之路，有高峰、有低谷，也有讓我重新愛上它的那個轉折點。
              </p>
            </div>

            {/* Timeline */}
            <div className="relative">
              {/* Vertical line (desktop) */}
              <div className="hidden lg:block absolute left-[160px] top-0 bottom-0 w-px bg-[#ede5d8]"></div>

              <div className="space-y-10">
                {milestones.map((m, i) => (
                  <div
                    key={i}
                    className={`flex flex-col lg:flex-row gap-6 lg:gap-0 transition-all duration-700`}
                    style={{ transitionDelay: `${i * 0.08}s` }}
                  >
                    {/* Year label */}
                    <div className="lg:w-40 lg:shrink-0 lg:pt-1 flex lg:flex-col lg:items-end lg:pr-8 items-center gap-4">
                      <span
                        className="font-serif-tc text-[#b08060] font-semibold whitespace-nowrap"
                        style={{ fontSize: '1.0625rem' }}
                      >
                        {m.year}
                      </span>
                    </div>

                    {/* Dot (desktop) */}
                    <div className="hidden lg:flex items-start pt-1.5 shrink-0">
                      <div className="relative flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-[#c8a96e] ring-4 ring-[#faf8f5]"></div>
                      </div>
                    </div>

                    {/* Content card */}
                    <div className="flex-1 lg:pl-8">
                      <div className="bg-[#f5f0e8] rounded-2xl border border-[#ede5d8] p-6 hover:border-[#c8b49a] transition-colors">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white shrink-0">
                            <i className={`${m.icon} text-[#7a6248] text-lg`}></i>
                          </div>
                          <div>
                            <h3
                              className="font-serif-tc text-[#2c2420] mb-2 leading-[1.4]"
                              style={{ fontSize: '1.125rem' }}
                            >
                              {m.title}
                            </h3>
                            <p className="text-[0.9375rem] leading-[1.75] text-[#5a4e42]">
                              {m.desc}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Teaching Philosophy Callout */}
            <div className="mt-16 bg-[#f2ede5] rounded-2xl border border-[#ede5d8] p-8 lg:p-10">
              <div className="flex flex-col lg:flex-row gap-8 items-center">
                <div className="w-20 h-20 lg:w-24 lg:h-24 flex items-center justify-center rounded-full bg-[#faf8f5] shrink-0">
                  <i className="ri-quill-pen-line text-[#7a6248] text-3xl"></i>
                </div>
                <div>
                  <p className="text-[0.8125rem] tracking-[0.15em] text-[#9a8470] font-medium uppercase mb-3">
                    教學理念的來源
                  </p>
                  <blockquote
                    className="font-serif-tc text-[#2c2420] leading-[1.65] italic"
                    style={{ fontSize: 'clamp(1.0625rem, 2vw, 1.25rem)' }}
                  >
                    「我用的方式，是我希望當年有人用來教我的方式。不催促、不比較、從你真實的狀態出發。我走過那段窒息的學習，所以我知道怎麼讓它不發生在你身上。」
                  </blockquote>
                  <p className="mt-4 text-[0.9375rem] text-[#9a8470]">— 鳴真，鳴箏創辦老師</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
